/* eslint-disable max-lines -- FIG export orchestration keeps shared GUID state in one pipeline */
import type { CanvasKit } from 'canvaskit-wasm'
import { deflateSync, inflateSync } from 'fflate'
import { toUint8Array } from 'js-base64'

import { compressFigDataSync } from '@open-pencil/fig'
import {
  buildComponentPropIndex,
  placeSlotContent,
  bytesToHex,
  exportCanvasGuides,
  importCanvasGuides,
  stringToGuid,
  type FigNodeChangeExportRuntime
} from '@open-pencil/fig/node-change'
import { initCodec, getCompiledSchema, getSchemaBytes } from '@open-pencil/kiwi/fig/codec'
import type { NodeChange } from '@open-pencil/kiwi/fig/codec'
import { decodeBinarySchema, compileSchema, ByteBuffer } from '@open-pencil/kiwi/schema-runtime'
import {
  ownsSlotContent,
  readBehaviour,
  renameBehaviourProperties,
  withBehaviour,
  type SceneGraph
} from '@open-pencil/scene-graph'
import { fractionalPosition } from '@open-pencil/scene-graph/order-keys'
import type { GUID } from '@open-pencil/scene-graph/primitives'

import type { SkiaRenderer } from '#core/canvas'
import { withFigExportRuntime } from '#core/canvas/text/shape'
import { CANVAS_BG_COLOR, IS_BROWSER, IS_TAURI } from '#core/constants'
import { applyEnabledLibrariesPluginData } from '#core/io/formats/fig/library-metadata'
import { findFigThumbnailPageId } from '#core/io/formats/fig/thumbnail-page'
import { renderThumbnail } from '#core/io/formats/raster/index.override'
import {
  getLazyFigImportContext,
  populateAllLazyFigImportRoots,
  populateLazyFigImportRootsChunked
} from '#core/kiwi/fig/lazy-import.override'
import {
  sceneNodeToKiwi,
  buildFontDigestMap,
  makeDocumentNodeChange,
  makeCanvasNodeChange
} from '#core/kiwi/fig/node-change/serialize'
import { normalizeImportedPageOrigins } from '#core/kiwi/fig/normalize-page-origin'
import { cloneSceneGraphForFigExport } from '#core/kiwi/fig/parse/transfer'
import { populateReaderExport } from '#core/kiwi/fig/session/document-state'
import { originalFigArchive } from '#core/kiwi/fig/session/original-archive'

import {
  appendVariableNodeChanges,
  sequentialPositions,
  assignSharedStyleGuids,
  assignVariableGuid,
  assignVariableGuids
} from './variable-export'

const THUMBNAIL_1X1 = toUint8Array(
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=='
)

/** Keep the editor interactive while export does multi-second sync work. */
function yieldExportSlice(): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, 0)
  })
}

type ElectronDesktopBridge = {
  invoke(command: string, args?: unknown): Promise<unknown>
}

/**
 * Electron stubs `__TAURI_INTERNALS__` so `IS_TAURI` is true. Going through
 * `@tauri-apps/api` still runs Tauri's Uint8Array → `Array.from` JSON path and
 * freezes the renderer on multi-MB saves (common when the shell loads the
 * deployed https:// frontend). Call `openPencilDesktop.invoke` with raw bytes.
 */
function isElectronDesktopStub(): boolean {
  if (typeof window === 'undefined') return false
  const internals = (
    window as Window & { __TAURI_INTERNALS__?: { openPencilElectron?: unknown } }
  ).__TAURI_INTERNALS__
  return Boolean(internals?.openPencilElectron)
}

function electronDesktopBridge(): ElectronDesktopBridge | null {
  if (typeof window === 'undefined') return null
  const bridge = (window as Window & { openPencilDesktop?: ElectronDesktopBridge })
    .openPencilDesktop
  return bridge ?? null
}

/** Convert bytes for Tauri JSON IPC without holding the main thread for the whole buffer. */
async function bytesToJsonNumberArray(bytes: Uint8Array): Promise<number[]> {
  const out = new Array<number>(bytes.byteLength)
  const chunk = 64 * 1024
  for (let offset = 0; offset < bytes.byteLength; offset++) {
    out[offset] = bytes[offset] ?? 0
    if ((offset + 1) % chunk === 0) await yieldExportSlice()
  }
  return out
}

function copyBytesForTransfer(bytes: Uint8Array): Uint8Array {
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  return copy
}

async function buildFigArchiveViaElectron(
  schemaDeflated: Uint8Array,
  kiwiData: Uint8Array,
  thumbnailPNG: Uint8Array,
  metaJSON: string,
  imageEntries: Array<{ name: string; data: Uint8Array }>,
  figKiwiVersion: number | undefined
): Promise<Uint8Array | null> {
  const bridge = electronDesktopBridge()
  if (!bridge) return null
  await yieldExportSlice()
  const result = await bridge.invoke('build_fig_file', {
    schemaDeflated,
    kiwiData,
    thumbnailPng: thumbnailPNG,
    metaJson: metaJSON,
    images: imageEntries.map((entry) => ({ name: entry.name, data: entry.data })),
    figKiwiVersion
  })
  if (result instanceof ArrayBuffer) return new Uint8Array(result)
  if (result instanceof Uint8Array) return result
  throw new Error('build_fig_file returned an unexpected payload')
}

type KiwiNodeChange = NodeChange & Record<string, unknown>
type FigExportPage = ReturnType<SceneGraph['getPages']>[number]

interface CanvasExportEntry {
  page: FigExportPage
  canvasGuid: GUID
  canvasNc: KiwiNodeChange
}

function collectImageEntries(graph: SceneGraph): Array<{ name: string; data: Uint8Array }> {
  const entries: Array<{ name: string; data: Uint8Array }> = []
  for (const [hash, data] of graph.images) {
    if (!hash || data.byteLength === 0) continue
    // Copy so zip/worker structured-clone never sees a detached/offset view.
    const copy = new Uint8Array(data.byteLength)
    copy.set(data)
    entries.push({ name: `images/${hash}`, data: copy })
  }
  return entries
}

const THUMBNAIL_WIDTH = 512
const THUMBNAIL_HEIGHT = 512

async function renderFigThumbnail(
  graph: SceneGraph,
  pageId: string | undefined,
  ck?: CanvasKit,
  renderer?: SkiaRenderer,
  renderHeadless = false
): Promise<Uint8Array> {
  if (!pageId) return THUMBNAIL_1X1
  if (ck && renderer) {
    return (
      renderThumbnail(ck, renderer, graph, pageId, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT) ??
      THUMBNAIL_1X1
    )
  }
  if (!renderHeadless || IS_BROWSER || IS_TAURI) return THUMBNAIL_1X1
  const { headlessRenderThumbnail } = await import('#core/io/formats/raster/index.override')
  return (
    (await headlessRenderThumbnail(graph, pageId, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT)) ??
    THUMBNAIL_1X1
  )
}

interface ComponentPropertyGuidState {
  ids: string[]
  maxLocalId0: number
  maxLocalId1: number
}

function collectComponentPropertyGuidState(graph: SceneGraph): ComponentPropertyGuidState {
  const ids = new Set<string>()
  let maxLocalId0 = 0
  let maxLocalId1 = 0
  for (const node of graph.getAllNodes()) {
    for (const definition of node.componentPropertyDefinitions) ids.add(definition.id)
    for (const reference of node.componentPropertyReferences) ids.add(reference.propertyId)
    for (const propertyId of Object.keys(node.componentPropertyAssignments)) ids.add(propertyId)
    for (const spec of node.variantPropSpecs) ids.add(spec.propDefId)
  }
  for (const propertyId of ids) {
    const match = /^(\d+):(\d+)$/.exec(propertyId)
    if (!match) continue
    const sessionID = Number.parseInt(match[1], 10)
    const localID = Number.parseInt(match[2], 10)
    if (sessionID === 0) maxLocalId0 = Math.max(maxLocalId0, localID)
    if (sessionID === 1) maxLocalId1 = Math.max(maxLocalId1, localID)
  }
  return { ids: [...ids], maxLocalId0, maxLocalId1 }
}

function assignComponentPropertyGuids(
  propertyIds: readonly string[],
  localIdCounter: { value: number },
  propertyIdToGuid: Map<string, GUID>,
  assignedGuidValues: Set<string>,
  nodeSourceGuidValues: Set<string>
): void {
  for (const propertyId of propertyIds) {
    const guid = assignVariableGuid(
      propertyId,
      localIdCounter,
      assignedGuidValues,
      nodeSourceGuidValues
    )
    propertyIdToGuid.set(propertyId, guid)
  }
}

/** Behaviours bind component properties by id, so they follow the ids' new GUIDs. */
function renameBehaviourPropertyIds(graph: SceneGraph, propertyIdToGuid: Map<string, GUID>): void {
  const rename = (propertyId: string) => {
    const guid = propertyIdToGuid.get(propertyId)
    return guid ? `${guid.sessionID}:${guid.localID}` : propertyId
  }
  for (const node of graph.getAllNodes()) {
    const behaviour = readBehaviour(node)
    if (behaviour)
      graph.updateNode(node.id, {
        pluginData: withBehaviour(node, renameBehaviourProperties(behaviour, rename))
      })
  }
}

function applyImportedCanvasFields(page: FigExportPage, canvasNc: KiwiNodeChange): void {
  if ('backgroundColor' in page.source.fig.rawNodeFields) {
    canvasNc.backgroundColor = structuredClone(page.source.fig.rawNodeFields.backgroundColor)
  }
  if ('backgroundPaints' in page.source.fig.rawNodeFields) {
    canvasNc.backgroundPaints = structuredClone(
      page.source.fig.rawNodeFields.backgroundPaints
    ) as NodeChange['backgroundPaints']
  }
  if (!page.source.id) return
  if (!('pageType' in page.source.fig.rawNodeFields)) delete canvasNc.pageType
  if (page.guides.length > 0) {
    const normalized = exportCanvasGuides(page.guides)
    const raw = page.source.fig.rawNodeFields.guides
    canvasNc.guides =
      Array.isArray(raw) && JSON.stringify(importCanvasGuides(raw)) === JSON.stringify(page.guides)
        ? structuredClone(raw)
        : normalized
  }
  const strokeJoin = page.source.fig.rawNodeFields.strokeJoin
  if (typeof strokeJoin === 'string') canvasNc.strokeJoin = strokeJoin
  const strokeWeight = page.source.fig.rawNodeFields.strokeWeight
  if (typeof strokeWeight === 'number') canvasNc.strokeWeight = strokeWeight
}

function buildCanvasEntries(
  graph: SceneGraph,
  pages: FigExportPage[],
  docGuid: GUID,
  localIdCounter: { value: number },
  nodeIdToGuid: Map<string, GUID>,
  assignedGuidValues: Set<string>
): { canvasEntries: CanvasExportEntry[]; internalCanvasGuid: GUID | null } {
  const canvasEntries: CanvasExportEntry[] = []
  let internalCanvasGuid: GUID | null = null
  for (let p = 0; p < pages.length; p++) {
    const page = pages[p]
    const canvasGuid = (() => {
      if (!page.source.id) return { sessionID: 0, localID: localIdCounter.value++ }

      const importedGuid = stringToGuid(page.source.id)
      const key = `${importedGuid.sessionID}:${importedGuid.localID}`

      if (!assignedGuidValues.has(key)) return importedGuid

      return { sessionID: 0, localID: localIdCounter.value++ }
    })()
    // Advance counter past any source.id-derived GUID to prevent collisions
    // with subsequently generated variable/collection GUIDs.
    if (page.source.id && canvasGuid.sessionID === 0) {
      localIdCounter.value = Math.max(localIdCounter.value, canvasGuid.localID + 1)
    }
    nodeIdToGuid.set(page.id, canvasGuid)
    assignedGuidValues.add(`${canvasGuid.sessionID}:${canvasGuid.localID}`)
    if (page.internalOnly) internalCanvasGuid = canvasGuid

    const canvasNc = makeCanvasNodeChange(
      canvasGuid,
      docGuid,
      page.source.orderKey ?? fractionalPosition(p),
      page.name,
      {
        backgroundOpacity: 1,
        backgroundColor: { ...CANVAS_BG_COLOR },
        backgroundEnabled: true
      }
    )
    applyImportedCanvasFields(page, canvasNc)
    if (page.internalOnly) canvasNc.internalOnly = true
    canvasEntries.push({ page, canvasGuid, canvasNc })
  }

  const hasSharedStyles = [...graph.nodes.values()].some((node) => node.sharedStyleType !== null)
  const hasSlotContent = [...graph.nodes.values()].some((node) => ownsSlotContent(graph, node))
  if (
    (graph.variableCollections.size > 0 || hasSharedStyles || hasSlotContent) &&
    internalCanvasGuid === null
  ) {
    internalCanvasGuid = { sessionID: 0, localID: localIdCounter.value++ }
    assignedGuidValues.add(`${internalCanvasGuid.sessionID}:${internalCanvasGuid.localID}`)
    canvasEntries.push({
      page: { id: '', name: 'Internal Only Canvas', internalOnly: true } as FigExportPage,
      canvasGuid: internalCanvasGuid,
      canvasNc: makeCanvasNodeChange(
        internalCanvasGuid,
        docGuid,
        fractionalPosition(canvasEntries.length),
        'Internal Only Canvas',
        { internalOnly: true }
      )
    })
  }

  return { canvasEntries, internalCanvasGuid }
}

interface InternalResourceContext {
  graph: SceneGraph
  nodeChanges: KiwiNodeChange[]
  internalCanvasGuid: GUID | null
  localIdCounter: { value: number }
  blobs: Uint8Array[]
  nodeIdToGuid: Map<string, GUID>
  fontDigestMap: Map<string, Uint8Array>
  varIdToGuid: Map<string, GUID>
  modeIdToGuid: Map<string, GUID>
  glyphBlobMap: Map<string, number>
  blobIndexByHex: Map<string, number>
  assignedGuidValues: Set<string>
  componentPropertyDefinitionsById: ReturnType<typeof buildComponentPropIndex>
  propertyIdToGuid: Map<string, GUID>
  runtime: FigNodeChangeExportRuntime
}

/**
 * Children already written under a canvas. Shared styles, variables and the canvas's own
 * layers all append to the internal canvas from separate passes, so each continues this
 * count rather than numbering from zero and handing siblings the same order key.
 */
function countCanvasChildren(changes: readonly NodeChange[], canvas: GUID): number {
  let count = 0
  for (const change of changes) {
    const parent = change.parentIndex?.guid
    if (parent?.sessionID === canvas.sessionID && parent.localID === canvas.localID) count++
  }
  return count
}

function appendInternalResources(context: InternalResourceContext): void {
  const { graph, internalCanvasGuid, nodeChanges } = context
  if (!internalCanvasGuid) return
  const written = countCanvasChildren(nodeChanges, internalCanvasGuid)
  const sharedStyleNodes = [...graph.nodes.values()].filter((node) => node.sharedStyleType !== null)
  assignSharedStyleGuids(
    sharedStyleNodes,
    context.localIdCounter,
    context.nodeIdToGuid,
    context.assignedGuidValues
  )
  for (let index = 0; index < sharedStyleNodes.length; index++) {
    nodeChanges.push(
      ...sceneNodeToKiwi(
        sharedStyleNodes[index],
        internalCanvasGuid,
        written + index,
        context.localIdCounter,
        graph,
        context.blobs,
        {
          nodeIdToGuid: context.nodeIdToGuid,
          fontDigestMap: context.fontDigestMap,
          varIdToGuid: context.varIdToGuid,
          glyphBlobMap: context.glyphBlobMap,
          blobIndexByHex: context.blobIndexByHex,
          assignedGuidValues: context.assignedGuidValues,
          componentPropertyDefinitionsById: context.componentPropertyDefinitionsById,
          modeIdToGuid: context.modeIdToGuid,
          propertyIdToGuid: context.propertyIdToGuid,
          runtime: context.runtime
        }
      )
    )
  }
  if (graph.variableCollections.size > 0) {
    appendVariableNodeChanges(
      graph,
      nodeChanges,
      internalCanvasGuid,
      context.varIdToGuid,
      context.modeIdToGuid,
      sequentialPositions(written + sharedStyleNodes.length)
    )
  }
}

export type ExportFigFileOptions = {
  /**
   * When true (default), an unmodified imported archive may be returned as-is.
   * Cloud / recovery saves must pass false so live graph blobs and images are
   * always re-encoded into the `.fig`.
   */
  reuseOriginalArchive?: boolean
}

export async function exportFigFile(
  sourceGraph: SceneGraph,
  ck?: CanvasKit,
  renderer?: SkiaRenderer,
  pageId?: string,
  renderHeadlessThumbnail = false,
  options?: ExportFigFileOptions
): Promise<Uint8Array> {
  const reuseOriginalArchive = options?.reuseOriginalArchive !== false
  if (reuseOriginalArchive) {
    const originalArchive = await originalFigArchive(sourceGraph)
    if (originalArchive) return originalArchive.slice()
  }

  return withFigExportRuntime(sourceGraph, ck, (runtime) =>
    writeFigFile(
      sourceGraph,
      runtime,
      ck,
      renderer,
      pageId,
      renderHeadlessThumbnail,
      options
    )
  )
}

async function writeFigFile(
  sourceGraph: SceneGraph,
  runtime: FigNodeChangeExportRuntime,
  ck: CanvasKit | undefined,
  renderer: SkiaRenderer | undefined,
  pageId: string | undefined,
  renderHeadlessThumbnail: boolean,
  options?: ExportFigFileOptions
): Promise<Uint8Array> {
  void options
  // Clone + expand can take seconds on large docs — yield around each phase so
  // pan/zoom/selection stay responsive during Save.
  await yieldExportSlice()
  const graph = cloneSceneGraphForFigExport(sourceGraph)
  await yieldExportSlice()

  const lazy = getLazyFigImportContext(graph)
  if (lazy?.materializePage) {
    const rootIds = graph.getPages(true).map((page) => page.id)
    await populateLazyFigImportRootsChunked(graph, rootIds, {
      materializeBudgetMs: 8,
      populateBudgetMs: 8,
      yieldBetween: yieldExportSlice
    })
  } else {
    // Worker-transfer stubs have no materialize hook; keep the sync fallback.
    populateAllLazyFigImportRoots(graph)
  }
  // Export populate rematerializes from kiwi — fix CANVAS / child x,y directly
  // on this clone so Ctrl+S writes corrected positions (no changeMap edits).
  normalizeImportedPageOrigins(graph)
  await yieldExportSlice()
  await initCodec()
  await yieldExportSlice()

  // When the document was imported from a .fig file, preserve the original
  // kiwi schema for both encoding and embedding. For the current version of
  // Figma, likely for quite some time, schema has more types/fields than our
  // subset, and using our schema to encode would produce field IDs that don't
  // align with the embedded schema. By compiling and using the original
  // schema, we improve the roundtrip-ability... This requires further work.
  let compiled: ReturnType<typeof getCompiledSchema>
  let schemaDeflated: Uint8Array
  if (graph.figSchemaDeflated) {
    const schemaBytes = inflateSync(graph.figSchemaDeflated)
    const figSchema = decodeBinarySchema(new ByteBuffer(schemaBytes))
    compiled = compileSchema(figSchema) as ReturnType<typeof getCompiledSchema>
    schemaDeflated = graph.figSchemaDeflated
  } else {
    compiled = getCompiledSchema()
    schemaDeflated = deflateSync(getSchemaBytes())
  }

  const docGuid = { sessionID: 0, localID: 0 }
  const localIdCounter = { value: 2 }

  const documentNc = makeDocumentNodeChange(docGuid, graph.documentColorSpace)
  const rootNode = graph.getNode(graph.rootId)
  if (rootNode) Object.assign(documentNc, rootNode.source.fig.rawNodeFields)
  applyEnabledLibrariesPluginData(documentNc, graph)
  const nodeChanges: KiwiNodeChange[] = [documentNc]

  // Seed import-session blobs so raw numeric blob indices and rematerialized
  // geometry both resolve into the encoded kiwi message.
  const lazyBlobs = getLazyFigImportContext(graph)?.blobs
  const blobs: Uint8Array[] = lazyBlobs?.length ? [...lazyBlobs] : []
  const pages = graph.getPages(true)
  const nodeIdToGuid = new Map<string, GUID>()
  const assignedGuidValues = new Set<string>()
  // Reserve the document GUID to prevent imported nodes with source.id "0:0"
  // from reusing the document's own GUID slot.
  assignedGuidValues.add(`${docGuid.sessionID}:${docGuid.localID}`)
  const varIdToGuid = new Map<string, GUID>()
  const modeIdToGuid = new Map<string, GUID>()
  const propertyIdToGuid = new Map<string, GUID>()
  const fontDigestMap = await buildFontDigestMap(graph)
  const glyphBlobMap = new Map<string, number>()
  const blobIndexByHex = new Map<string, number>()
  // Deduplicate against seeded lazy blobs by content hash.
  for (let index = 0; index < blobs.length; index++) {
    const bytes = blobs[index]
    if (!bytes || bytes.byteLength === 0) continue
    blobIndexByHex.set(bytesToHex(bytes), index)
  }
  const componentPropertyDefinitionsById = buildComponentPropIndex(graph)

  // Scan ALL imported source.ids BEFORE any new GUID assignment to find
  // max sessionID:0 and sessionID:1 localID values. This guarantees the
  // counter is past every imported GUID before any canvas, variable, or
  // node claims a new counter-based GUID — preventing collisions.
  let maxLocalId0 = localIdCounter.value - 1
  let maxLocalId1 = localIdCounter.value - 1
  const nodeSourceGuidValues = new Set<string>()
  for (const node of graph.nodes.values()) {
    if (node.source.id) {
      nodeSourceGuidValues.add(node.source.id)
      const g = stringToGuid(node.source.id)
      if (g.sessionID === 0 && g.localID > maxLocalId0) {
        maxLocalId0 = g.localID
      }
      if (g.sessionID === 1 && g.localID > maxLocalId1) {
        maxLocalId1 = g.localID
      }
    }
  }
  const propertyGuidState = collectComponentPropertyGuidState(graph)
  maxLocalId0 = Math.max(maxLocalId0, propertyGuidState.maxLocalId0)
  maxLocalId1 = Math.max(maxLocalId1, propertyGuidState.maxLocalId1)
  localIdCounter.value = Math.max(localIdCounter.value, maxLocalId0 + 1, maxLocalId1 + 1)

  const { canvasEntries, internalCanvasGuid } = buildCanvasEntries(
    graph,
    pages,
    docGuid,
    localIdCounter,
    nodeIdToGuid,
    assignedGuidValues
  )

  // Assign variable GUIDs AFTER canvas entries so that source.id-derived
  // canvas GUIDs don't collide with generated variable GUIDs.
  assignVariableGuids(
    graph,
    localIdCounter,
    varIdToGuid,
    modeIdToGuid,
    assignedGuidValues,
    nodeSourceGuidValues
  )

  assignComponentPropertyGuids(
    propertyGuidState.ids,
    localIdCounter,
    propertyIdToGuid,
    assignedGuidValues,
    nodeSourceGuidValues
  )
  renameBehaviourPropertyIds(graph, propertyIdToGuid)

  for (const entry of canvasEntries) nodeChanges.push(entry.canvasNc)

  appendInternalResources({
    graph,
    nodeChanges,
    internalCanvasGuid,
    localIdCounter,
    blobs,
    nodeIdToGuid,
    fontDigestMap,
    varIdToGuid,
    modeIdToGuid,
    glyphBlobMap,
    blobIndexByHex,
    assignedGuidValues,
    componentPropertyDefinitionsById,
    propertyIdToGuid,
    runtime
  })

  const orderedCanvasEntries = [
    ...canvasEntries.filter((entry) => entry.page.internalOnly),
    ...canvasEntries.filter((entry) => !entry.page.internalOnly)
  ]
  const slotContentRecords: KiwiNodeChange[] = []
  for (const { page, canvasGuid } of orderedCanvasEntries) {
    const children = graph
      .getChildren(page.id)
      .filter((child) => !child.internalOnly && child.sharedStyleType === null)
    const base = countCanvasChildren(nodeChanges, canvasGuid)
    for (let i = 0; i < children.length; i++) {
      nodeChanges.push(
        ...sceneNodeToKiwi(children[i], canvasGuid, base + i, localIdCounter, graph, blobs, {
          nodeIdToGuid,
          fontDigestMap,
          varIdToGuid,
          glyphBlobMap,
          blobIndexByHex,
          assignedGuidValues,
          componentPropertyDefinitionsById,
          modeIdToGuid,
          propertyIdToGuid,
          slotContentRecords,
          runtime
        })
      )
      // Large pages encode thousands of NCs — yield every few top-level roots.
      if ((i + 1) % 4 === 0) await yieldExportSlice()
    }
    await yieldExportSlice()
  }
  if (internalCanvasGuid) {
    const first = countCanvasChildren(nodeChanges, internalCanvasGuid)
    placeSlotContent(slotContentRecords, internalCanvasGuid, first, fractionalPosition)
    nodeChanges.push(...slotContentRecords)
  }

  const msg: Record<string, unknown> = {
    type: 'NODE_CHANGES',
    sessionID: 0,
    ackID: 0,
    nodeChanges
  }

  if (blobs.length > 0) {
    msg.blobs = blobs.map((bytes) => ({ bytes }))
  }

  await yieldExportSlice()
  const kiwiData = compiled.encodeMessage(msg)
  await yieldExportSlice()

  const currentPageId = pageId ?? findFigThumbnailPageId(pages)
  const thumbnailPNG = await renderFigThumbnail(
    graph,
    currentPageId,
    ck,
    renderer,
    renderHeadlessThumbnail
  )

  const metaJSON = JSON.stringify({
    version: 1,
    app: 'OpenPencil',
    createdAt: new Date().toISOString()
  })

  const imageEntries = collectImageEntries(graph)

  const version = graph.figKiwiVersion ?? undefined

  // Electron + remote https:// frontend: bypass Tauri JS Array.from IPC.
  if (isElectronDesktopStub()) {
    const viaElectron = await buildFigArchiveViaElectron(
      schemaDeflated,
      kiwiData,
      thumbnailPNG,
      metaJSON,
      imageEntries,
      version
    )
    if (viaElectron) return viaElectron
  }

  // Real Tauri uses native zip (chunked number arrays keep the UI breathing).
  if (IS_TAURI && !isElectronDesktopStub()) {
    const { invoke } = await import('@tauri-apps/api/core')
    const images = []
    for (const entry of imageEntries) {
      images.push({ name: entry.name, data: await bytesToJsonNumberArray(entry.data) })
    }
    return new Uint8Array(
      await invoke<ArrayBuffer>('build_fig_file', {
        schemaDeflated: await bytesToJsonNumberArray(schemaDeflated),
        kiwiData: await bytesToJsonNumberArray(kiwiData),
        thumbnailPng: await bytesToJsonNumberArray(thumbnailPNG),
        metaJson: metaJSON,
        images,
        figKiwiVersion: version
      })
    )
  }

  return compressFigData(schemaDeflated, kiwiData, thumbnailPNG, metaJSON, imageEntries, version)
}

export { compressFigDataSync } from '@open-pencil/fig'

function canUseWorker(): boolean {
  return typeof Worker !== 'undefined' && IS_BROWSER
}

async function compressViaWorker(
  schemaDeflated: Uint8Array,
  kiwiData: Uint8Array,
  thumbnailPNG: Uint8Array,
  metaJSON: string,
  imageEntries: Array<{ name: string; data: Uint8Array }>,
  figKiwiVersion?: number
): Promise<Uint8Array> {
  // Own copies so transferables cannot detach live ByteBuffer / image views.
  await yieldExportSlice()
  const schemaCopy = copyBytesForTransfer(schemaDeflated)
  const kiwiCopy = copyBytesForTransfer(kiwiData)
  const thumbCopy = copyBytesForTransfer(thumbnailPNG)
  const imageCopies = imageEntries.map((entry) => ({
    name: entry.name,
    data: copyBytesForTransfer(entry.data)
  }))
  await yieldExportSlice()

  return await new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./export-worker.ts', import.meta.url), {
      type: 'module'
    })

    worker.onmessage = (e: MessageEvent<Uint8Array>) => {
      resolve(e.data)
      worker.terminate()
    }
    worker.onerror = (err) => {
      reject(new Error(err.message))
      worker.terminate()
    }

    const transfer: Transferable[] = [
      schemaCopy.buffer,
      kiwiCopy.buffer,
      thumbCopy.buffer,
      ...imageCopies.map((entry) => entry.data.buffer)
    ]
    worker.postMessage(
      {
        schemaDeflated: schemaCopy,
        kiwiData: kiwiCopy,
        thumbnailPNG: thumbCopy,
        metaJSON,
        images: imageCopies,
        figKiwiVersion
      },
      transfer
    )
  })
}

export async function compressFigData(
  schemaDeflated: Uint8Array,
  kiwiData: Uint8Array,
  thumbnailPNG: Uint8Array,
  metaJSON: string,
  imageEntries: Array<{ name: string; data: Uint8Array }>,
  figKiwiVersion?: number
): Promise<Uint8Array> {
  if (canUseWorker()) {
    try {
      return await compressViaWorker(
        schemaDeflated,
        kiwiData,
        thumbnailPNG,
        metaJSON,
        imageEntries,
        figKiwiVersion
      )
    } catch (error) {
      console.warn('[fig] Export worker failed; compressing on the main thread', error)
    }
  }
  await yieldExportSlice()
  return compressFigDataSync(
    schemaDeflated,
    kiwiData,
    thumbnailPNG,
    metaJSON,
    imageEntries,
    figKiwiVersion
  )
}
